import React from 'react';
import {
    $generateHtmlFromNodes,
    $isRootTextContentEmpty,
    LexicalComposer,
    editorTheme,
    LinkNode,
    HeadingNode,
    ParagraphNode,
    ListNode,
    ListItemNode,
    HistoryPlugin,
    ToolbarPlugin,
    OnChangePlugin,
    ListPlugin,
    LinkPlugin,
    RichTextPlugin,
    LexicalErrorBoundary,
    RichContentEditable,
    PrefillPlugin,
    RTEInitValue,
    initialPrefillTag,
} from '@talentmesh/rte';
import * as yup from 'yup';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebounceCallback } from 'usehooks-ts';
import { RHFInput } from '@/Components/shadcn/rhf-input';
import { FieldGroup } from '@/Components/shadcn/field';
import { Separator } from '@/Components/shadcn/separator';
import { Spinner } from '@/Components/shadcn/spinner';
import { TypographyH2, TypographyH3, TypographyP } from '@/Components/shadcn';
import { Button } from '@/Components/shadcn/button';
import EmailTemplatePlaceholders from './EmailTemplatePlaceholders';
import { useNotificationContext } from '@/Context/NotificationContext';
import { useUserContext } from '@/Context/UserContext';
import User from '@/Models/User';
import UIStrings from '@/Utils/UIStrings';
import ContactSupportSection from './ContactSupportSection';
import { useRecruiterSignature, useUpdateRecruiterSignature } from '../api/useRecruiterSignature';
import PlaceholderPlugins, {
    Placeholder,
} from '../../Candidates/AiSearch/components/ShortlistedList/components/RTE/PlaceholderPlugins';
import YupValidationErrors from '@/Utils/YupValidationErrors';

interface AccountInfoFormInitialValues {
    firstName: string;
    lastName: string;
    signature: string;
}

const validationSchema = yup.object({
    firstName: yup.string().required(UIStrings.Required),
    lastName: yup.string().required(UIStrings.Required),
    signature: yup.string().required(UIStrings.Required).max(1000, YupValidationErrors.MaxSymbolsValidationError),
});

const signaturePlaceholders: Placeholder[] = [
    { label: UIStrings.FirstName, value: EmailTemplatePlaceholders.FirstName },
    { label: UIStrings.LastName, value: EmailTemplatePlaceholders.LastName },
    { label: UIStrings.CompanyName, value: EmailTemplatePlaceholders.CompanyName },
];

const AccountInfoForm = () => {
    const { user, updateUser } = useUserContext();
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const { data: recruiterSignature } = useRecruiterSignature();
    const { trigger: updateSignature } = useUpdateRecruiterSignature();

    const form = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onSubmit',
        defaultValues: { firstName: user.firstName, lastName: user.lastName, signature: '' },
    });

    const { fieldState: signatureFieldState } = useController({ control: form.control, name: 'signature' });
    const signatureFieldErrorMessage = signatureFieldState.error?.message;

    const submitHandler = async (values: AccountInfoFormInitialValues) => {
        const updatedAccountInfo: Omit<AccountInfoFormInitialValues, 'signature'> = { ...values };

        try {
            const updatedUser: User = {
                ...user,
                ...updatedAccountInfo,
            };

            await updateUser(updatedUser);
            await updateSignature({ signature: values.signature });

            showSuccessToaster(UIStrings.AccountDataSuccessfullyUpdated);
        } catch (error) {
            showFailToaster();
        }
    };

    const handleEditorOnChange = useDebounceCallback<Parameters<typeof OnChangePlugin>['0']['onChange']>(
        (editorState, editor, tags) => {
            editorState.read(() => {
                let signatureToSet = $generateHtmlFromNodes(editor);
                const isPrefill = tags.has(initialPrefillTag);

                if ($isRootTextContentEmpty(editor.isComposing(), true)) {
                    signatureToSet = '';
                }

                form.setValue('signature', signatureToSet, {
                    shouldValidate: true,
                    shouldDirty: !isPrefill,
                    shouldTouch: !isPrefill,
                });
            });
        },
        10,
    );

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(submitHandler)}>
                <div className="flex flex-col gap-6">
                    <TypographyH2 className="leading-none">{UIStrings.PersonalInformation}</TypographyH2>
                    <FieldGroup className="max-w-2xl gap-4">
                        <RHFInput
                            id="firstName"
                            name="firstName"
                            type="text"
                            label={UIStrings.FirstNameWithAesterisk}
                            fullWidth
                        />
                        <RHFInput
                            id="lastName"
                            name="lastName"
                            type="text"
                            label={UIStrings.LastNameWithAesterisk}
                            fullWidth
                        />
                    </FieldGroup>
                </div>
                <div data-cy="RecruiterSignatureSection" className="mt-6 flex flex-col gap-1.5">
                    <TypographyH3>{UIStrings.Signature}</TypographyH3>
                    <TypographyP>{UIStrings.ThisSignatureWillBeAddedAutomaticallyToAllOutreachEmails}</TypographyP>
                    <div className="mt-4 max-w-4xl">
                        <LexicalComposer
                            initialConfig={{
                                namespace: 'Recruiter signature editor',
                                onError: (error) => form.setError('signature', { message: error.message }),
                                theme: editorTheme,
                                nodes: [LinkNode, HeadingNode, ParagraphNode, ListNode, ListItemNode],
                            }}
                        >
                            <PrefillPlugin initialHtml={recruiterSignature?.signature ?? RTEInitValue} />
                            <HistoryPlugin />
                            <ToolbarPlugin features={['headingStyle', 'bold', 'italic', 'ol', 'ul']}>
                                <PlaceholderPlugins placeholders={signaturePlaceholders} />
                            </ToolbarPlugin>
                            <OnChangePlugin
                                ignoreHistoryMergeTagChange
                                ignoreSelectionChange
                                onChange={handleEditorOnChange}
                            />
                            <ListPlugin />
                            <LinkPlugin />
                            <RichTextPlugin
                                ErrorBoundary={LexicalErrorBoundary}
                                contentEditable={
                                    <RichContentEditable
                                        className={`max-h-32 min-h-32 rounded-b-xl p-3 text-sm [&_p]:text-sm [&_p]:leading-relaxed [&_p]:tracking-normal ${
                                            signatureFieldErrorMessage ? 'border-destructive border' : ''
                                        }`}
                                        style={{ overflowY: 'auto' }}
                                    />
                                }
                            />
                        </LexicalComposer>
                        {signatureFieldErrorMessage && (
                            <TypographyP className="text-destructive mt-1">{signatureFieldErrorMessage}</TypographyP>
                        )}
                    </div>
                    <Button disabled={!form.formState.isValid} className="mt-4 mb-6 max-w-max" type="submit">
                        {form.formState.isSubmitting && <Spinner className="mr-2 size-4" />}
                        {UIStrings.Save}
                    </Button>
                    <Separator className="my-2" />
                    <ContactSupportSection />
                </div>
            </form>
        </FormProvider>
    );
};

export default AccountInfoForm;
